import React, { useRef, useEffect, useMemo } from 'react'
import { Grid, TextField, Typography } from '@mui/material'
import clsx from 'clsx'
import { useStyles } from './CheckinModal.styles'
import { createLogger } from '../../../../app/logger'
import { useResponsiveInfo } from '../../../../hooks/useResponsiveInfo'
import {
  getDirtyTagSelector,
  getTagFieldErrorSelector,
  getSelectedEntrySelector,
  isTagError,
  setDirtyTag,
  setDirtyInput,
  setEnterKeyPress,
} from '../../checkinSlice'
import useAppDispatch from '../../../../hooks/useAppDispatch'
import useAppSelector from '../../../../hooks/useAppSelector'
import { extractBibValue } from '../../../../utils/extractBibValue'
import { debounce } from 'lodash'

// eslint-disable-next-line no-unused-vars
const logger = createLogger({
  prefix: 'EntryPanel',
})

// ==================================================================================================

export const EntryPanelTagField = () => {
  const dispatch = useAppDispatch()
  const entry = useAppSelector(getSelectedEntrySelector)
  const { classes } = useStyles()
  const { isMobile } = useResponsiveInfo()
  const ref = useRef<HTMLDivElement>(null)
  const tagFieldError = useAppSelector(getTagFieldErrorSelector)
  const error = isTagError(tagFieldError)
  const dirtyTag = useAppSelector(getDirtyTagSelector) || ''
  logger.debug('TagField', { dirtyTag })

  const debounceDirtyTag = useMemo(
    () => debounce((value) => {
      const updatedValue = extractBibValue(value)

      dispatch(setDirtyTag(updatedValue))
      dispatch(setDirtyInput(!!updatedValue))
    }, 100, { leading: false, trailing: true })
    , [dispatch])

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!entry?.checkedIn && e.key === 'Enter') {
        dispatch(setEnterKeyPress(true))
        debounceDirtyTag.flush()
      } else {
        dispatch(setEnterKeyPress(false))
      }
    },
    [entry?.checkedIn, dispatch, debounceDirtyTag]
  )

  useEffect(() => {
    if (dirtyTag && !entry?.checkedIn) {
      dispatch(setDirtyInput(true))
    }
  }, [dirtyTag, entry, dispatch])

  if (!!entry?.checkedIn && !error) {
    return (
      <Grid
        data-cy='checked-in-grid'
        container
        display='flex'
        direction='column'
        alignItems={'center'}
      >
        <TextField
          ref={ref}
          disabled={true}
          data-cy={'check-in-tag'}
          value={!dirtyTag ? 'No Tag' : dirtyTag}
          className={clsx(classes.bibField, classes.checkedInBib)}
        />
        <Typography className={classes.checkedInBibLabel}>
          Checked In
        </Typography>
      </Grid>
    )
  } else {
    const helperText = () => {
      if (isTagError(tagFieldError)) {
        return 'Tag is already assigned'
      }
    }
    return (
      <>
        <TextField
          ref={ref}
          data-cy={'check-in-tag'}
          value={dirtyTag}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            dispatch(setDirtyTag(e.target.value))
            debounceDirtyTag(e.target.value)
          }}
          className={clsx(
            error && classes.bibError,
            classes.bibField,
            isMobile && classes.mobileBib
          )}
          error={error}
          helperText={helperText()}
          type={'text'}
          inputMode={'numeric'}
          inputProps={{ pattern: '\\d*' }}
        />
      </>
    )
  }
}
